import React, { useRef, useState } from 'react';
import { Filter, IFilterProps, IFilterRef } from '../../Filter/Filter';
import { AuditionGrid, IAuditionGrid } from '../../AuditionComponents/AuditionGrid';
import { IAudition } from '../../../types/global/helper';
import { Box, Dialog, IconButton, Slide, Stack, Typography } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { Spinner } from '@chakra-ui/react';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export interface IAuditionListContainer extends IFilterProps, IAuditionGrid {
    auditions: IAudition[];
    loading?: boolean;
    onCenterEndReached?: () => void;
    onRightEndReached?: () => void;
}

export const AuditionListContainer: React.FC<IAuditionListContainer> = ({ auditions, loading, ...props }) => {
    const filterRef = useRef<IFilterRef>(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const { themeStyle } = useThemeContext();
    const scrollRef = useRef<HTMLDivElement>(null);
    const offset = 600;

    const synchroniseFilter = (filter: any) => {
        filterRef.current?.onUpdateFilter(filter);
    };

    const handleScroll = () => {
        const el = scrollRef.current;
        if (el) {
            const { scrollTop, scrollHeight, clientHeight } = el;
            if (scrollTop + clientHeight >= scrollHeight - offset) {
                props.onCenterEndReached?.();
            }
        }
    };

    return (
        <Box
            ref={scrollRef}
            onScroll={handleScroll}
            sx={{
                height: '100dvh',
                overflowY: 'auto',
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3 },
                pb: { xs: 3, sm: 4 },
            }}>
            {loading && (
                <Box display="flex" justifyContent="center" py={2}>
                    <Spinner h={8} w={8} color={theme[themeStyle].tag_white} />
                </Box>
            )}

            <AuditionGrid
                auditions={auditions}
                onUpdateFilter={synchroniseFilter}
                toggleFilterInMobile={() => setFilterOpen(true)}
                {...props}
            />

            {/* Filter Overlay */}
            <Dialog
                fullScreen
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                TransitionComponent={Transition}
                PaperProps={{
                    sx: {
                        backgroundColor: '#0e0e0e',
                        backgroundImage: 'none',
                    },
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
                    {/* Decorative blurs */}
                    <Box
                        sx={{
                            position: 'fixed',
                            top: '-10%',
                            right: '-10%',
                            width: '60%',
                            height: '60%',
                            background: `${theme[themeStyle].primary_orange}1A`,
                            filter: 'blur(120px)',
                            borderRadius: '50%',
                            pointerEvents: 'none',
                        }}
                    />

                    {/* Top Bar */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            px: 3,
                            py: 2,
                            backgroundColor: 'rgba(14,14,14,0.9)',
                            backdropFilter: 'blur(24px)',
                            zIndex: 50,
                            borderBottom: '1px solid rgba(19,19,19,0.2)',
                        }}>
                        <IconButton onClick={() => setFilterOpen(false)} sx={{ color: theme[themeStyle].tag_white }}>
                            <CloseIcon />
                        </IconButton>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 18,
                                color: theme[themeStyle].tag_white,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                            }}>
                            Filters
                        </Typography>
                        <Typography
                            onClick={() => {
                                props.onClearFilter?.();
                                setFilterOpen(false);
                            }}
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: theme[themeStyle].primary_orange,
                                cursor: 'pointer',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                            }}>
                            Clear All
                        </Typography>
                    </Stack>

                    {/* Filter Content */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            px: 3,
                            py: 4,
                            pb: 14,
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none',
                        }}>
                        <Box sx={{ '& > div': { maxWidth: '100% !important' } }}>
                            <Filter ref={filterRef} onClose={() => setFilterOpen(false)} {...props} />
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};
